/**
 * InboxLayout — the left panel of the inbox two-pane view.
 *
 * Renders the scrollable message list. The status tabs live in the Topbar
 * (published by the `/(workspace)/approvals` layout route via
 * `WorkspaceShell.sectionTabs`), so this panel is list-only. Row clicks
 * navigate to `/(workspace)/approvals/$approvalId` so the URL updates on
 * selection. The right pane is supplied by the caller as `children` (a
 * route <Outlet>).
 */
import { useNavigate, useParams } from "@tanstack/react-router";
import { Alert } from "@flint/ui";
import { trpc } from "../../lib/trpc.ts";
import { type InboxItem, type InboxStatus } from "./inbox-model.ts";
import { MessageRow } from "./message-row.tsx";

interface InboxLayoutProps {
  status: InboxStatus;
  children: React.ReactNode;
}

export function InboxLayout({ status, children }: InboxLayoutProps) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as Record<string, string | undefined>;
  const selectedId = params.approvalId ?? null;

  const list = trpc.approvals.list.useQuery({ limit: 50, status });
  const items = (list.data?.items ?? []) as InboxItem[];

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden md:grid-cols-[minmax(280px,320px)_minmax(0,1fr)]">
        {/* ── List panel ── */}
        <section
          aria-label="Messages"
          className="flex min-h-0 flex-col overflow-hidden border-r border-line bg-paper"
        >
          {/* List body */}
          <div className="min-h-0 flex-1 overflow-y-auto">
            {list.error ? (
              <div className="p-4">
                <Alert
                  intent="bad"
                  title="Couldn't load the inbox"
                  description={list.error.message}
                />
              </div>
            ) : !list.isLoading && items.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <p className="text-sm font-medium text-ink">
                  {status === "pending"
                    ? "Nothing waiting on you."
                    : `No ${status} items.`}
                </p>
                {status === "pending" ? (
                  <p className="mx-auto mt-1 max-w-xs text-xs text-muted">
                    Flagged actions — voiding an invoice, an over-limit payment, going
                    past a customer's credit line — land here for your decision.
                  </p>
                ) : null}
              </div>
            ) : (
              items.map((item) => (
                <MessageRow
                  key={item.id}
                  item={item}
                  selected={item.id === selectedId}
                  isFounder
                  onSelect={() => {
                    void navigate({
                      to: "/approvals/$approvalId",
                      params: { approvalId: item.id },
                      search: status !== "pending" ? { status } : {},
                    });
                  }}
                  onDecide={() => {}}
                  decidePending={false}
                />
              ))
            )}
          </div>
        </section>

        {/* ── Right pane — Outlet or empty state ── */}
        <section
          aria-label="Reading pane"
          className="hidden min-h-0 overflow-hidden md:block"
        >
          {children}
        </section>
      </div>
    </div>
  );
}
